import { useCallback, useEffect, useMemo, useState } from 'react'

import * as XLSX from 'xlsx'
import { flatten, safeParseFloat, zeroPadding } from 'shared/util'

import {
  Box, Stack, Typography, FormControl, TextField, Alert, Select, MenuItem,
  FormControlLabel, RadioGroup, Radio, FormGroup, Checkbox,
	Button
} from "@mui/material"

import { CloudUpload as CloudUploadIcon } from "@mui/icons-material"

import { Section } from 'components/controls/section'
import { NumericInput } from 'components/controls/numbericInput'

import { AddTargetListFormData } from 'interface/search/targetList'
import { useUserSession } from 'features/session/context'

const PERCENT_SUFFIX = '00.00%'
const ACCEPT_FILETYPE_EXCEL = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel'

type LoadFromType = 'file' | 'input_dcf' | 'input_pid'
type IdentifierType = 'kojin_cd' | 'personal_id'

export const useTargetListFileForm = () => {
	// formdata
  const [loadFrom, setLoadFrom] = useState<LoadFromType>('file')

	// formdata - file
  const [sheetName, setSheetName] = useState<string>('')
	const [startRow, setStartRow] = useState<string>('1')
  const [hasHeader, setHasHeader] = useState<boolean>(true)
  const [identifier, setIdentifier] = useState<IdentifierType>('kojin_cd')
  const [identifierColumn, setIdentifierColumn] = useState<string>('')
  const [extendScoreColumn, setExtendScoreColumn] = useState<string>('none')
  const [targetTypeColumn, setTargetTypeColumn] = useState<string>('none')
  const [targetTypeValueMap, setTargetTypeValueMap] = useState<{[index: string]: string }>({})
	const [categoryColumns, setCategoryColumns] = useState<string[]>([])
	const [scoreColumns, setScoreColumns] = useState<string[]>([])

	// formdata - input_dcf/pid
  const [rowText, setRowText] = useState<string>('')

	// readonly
	const [loading, setLoading] = useState<boolean>(false)
  const [file, setFile] = useState<File | null>(null)
  const [workBook, setWorkBook] = useState<XLSX.WorkBook | null>(null)
  const [sheetNameList, setSheetNameList] = useState<string[]>([])
  const [columnList, setColumnList] = useState<ColumnInfo[]>([])

	const addCategoryColumn = useCallback(
		(column: string) => {
			const columns = [...categoryColumns]
			columns.push(column)
			setCategoryColumns(columns)
		},
		[categoryColumns]
	)
	const editCategoryColumn = useCallback(
		(sequence: number, column: string) => {
			const columns = [...categoryColumns]
			columns[sequence] = column
			setCategoryColumns(columns)
		},
		[categoryColumns]
	)
	const removeCategoryColumn = useCallback(
		(sequence: number) => {
			const columns = categoryColumns.flatMap((column, i) => i === sequence ? [] : column)
			setCategoryColumns([...columns])
		},
		[categoryColumns]
	)

	const addScoreColumn = useCallback(
		(column: string) => {
			const columns = [...scoreColumns]
			columns.push(column)
			setScoreColumns(columns)
		},
		[scoreColumns]
	)
	const editScoreColumn = useCallback(
		(sequence: number, edittedColumn: string) => {
			const columns = scoreColumns.map((column, i) => i === sequence ? edittedColumn : column)
			setScoreColumns([...columns])
		},
		[scoreColumns]
	)
	const removeScoreColumn = useCallback(
		(sequence: number) => {
			const columns = scoreColumns.flatMap((column, i) => i === sequence ? [] : column)
			setScoreColumns([...columns])
		},
		[scoreColumns]
	)

	// dialog
  const [confirm, setConfirm] = useState<{
    title: string,
    onOk: () => void
  } | null>(null);

	const rows = useMemo(
		() => rowText.split('\n').flatMap(
			(row) => {
				const cells = row.split('\t')
				if (cells.length === 0) return []
				const identifier = cells[0].trim()
				if (identifier.length === 0) return []
				return {
					identifier,
					other: cells.length > 1 ? cells.slice(1) : undefined
				}
			}
		),
    [rowText]
  )

	const isDisabled = useMemo<boolean>(
    () => {
      if (loadFrom === 'file') return file === null
      if (loadFrom === 'input_dcf' || loadFrom === 'input_pid') return rows.length === 0
			return true
    },
    [loadFrom, file, rows]
  )

	// 選択したworkBook内のシート名の解析
	useEffect(
		() => {
			setSheetNameList(workBook?.SheetNames ?? [])
			setSheetName((workBook && workBook.SheetNames.length > 0) ? workBook.SheetNames[0] : '')
		},
		[workBook]
	)

	// 選択したシート内の解析
  useEffect(
		() => {
			if (workBook && sheetName) {
				const sheet = workBook.Sheets[sheetName]
				const columnList: ColumnInfo[] = []
				const headerRow = (parseInt(startRow) ?? 1) - 1
				for(let columnIndex = 0; columnIndex < 1000; columnIndex++) {
					const cellAddress = XLSX.utils.encode_cell({ c: columnIndex, r: headerRow })
					const cell = sheet[cellAddress]
					if (cell === null || cell === undefined) break
					columnList.push({
						index: columnIndex,
						name: XLSX.utils.encode_col(columnIndex),
						label: (hasHeader ? cellValue(cell) : null) ?? `Column ${columnIndex}`
					})
				}
				setColumnList(columnList)
				if (columnList.length > 0) {
					setIdentifierColumn(columnList[0].name)
				}
			} else {
				setColumnList([])
			}
			setTargetTypeColumn('none')
			setExtendScoreColumn('none')
		},
		[
			workBook, sheetName, startRow, hasHeader,
			setColumnList, setTargetTypeColumn, setExtendScoreColumn
		]
	)

	// 分類指定に用いるカラムの値を列挙する
  const targetTypeConditions = useMemo(
		() => {
			if (workBook && targetTypeColumn && targetTypeColumn !== 'none') {
				const sheet = workBook.Sheets[sheetName]
				const columnIndex = columnList.find((col) => col.name === targetTypeColumn)?.index
				if (columnIndex !== undefined) {
					const valueList: string[] = []
					let hasNull = false
					const rowStart = ((parseInt(startRow) ?? 1) - 1) + (hasHeader ? 1 : 0)
					for(let rowIndex = rowStart; rowIndex < 999999; rowIndex++) {
						const cellAddress = XLSX.utils.encode_cell({ c: columnIndex, r: rowIndex })
						const cell = sheet[cellAddress]
						if (cell === null || cell === undefined) break
						const v = cellValue(cell)
						if (v === null) {
							hasNull = true
						} else if (typeof v === 'string') {
							if (valueList.indexOf(v) < 0) valueList.push(v)
						} else if (typeof v === 'number') {
							const text = `${v}`
							if (valueList.indexOf(text) < 0) valueList.push(text)
						}
					}
					valueList.sort((a, b) => parseInt(a) - parseInt(b))
					if (hasNull) {
						valueList.unshift('(空)')
					}
					return valueList
				}
			}
			return []
		},
		[workBook, sheetName, columnList, targetTypeColumn, startRow, hasHeader]
	)

  const submit = useCallback(
    (post: (postData: AddTargetListFormData) => void) => {
			let duplicate = 0
			const postData: AddTargetListFormData = {
				identifier,
				data: []
			}
			if (loadFrom === 'file') {
        if (!workBook) return

				const sheet = workBook.Sheets[sheetName]
        if (!sheet) return

				const identifierIndex = columnList.find((col) => col.name === identifierColumn)?.index ?? null
        if (identifierIndex === null) return

				const getCellValue = (column: number, row: number) => {
          const address = XLSX.utils.encode_cell({ c: column, r: row })
          const cell = sheet[address]
          if (cell === null || cell === undefined) return undefined
          const value = cellValue(cell)
          if (value === null) return undefined
					return value
				}
				const extendIndex = (extendScoreColumn && extendScoreColumn !== 'none') ? (columnList.find((col) => col.name === extendScoreColumn)?.index ?? null) : null
        const targetTypeIndex = (targetTypeColumn && targetTypeColumn !== 'none') ? (columnList.find((col) => col.name === targetTypeColumn)?.index ?? null) : null

				const categoryColumnList = flatten(categoryColumns.map((column) => columnList.find((col) => col.name === column)))
				if (categoryColumnList.length > 0) {
					postData.category = categoryColumnList.map(
						(column, i) => ({ sequence: i, title: column.label })
					)
				}
				const categories: { code: string, name: string }[][] = categoryColumnList.map((column) => {
					const values: string[] = []
					for(let rowIndex = 1; rowIndex < 999999; rowIndex++) {
						const categoryValue = getCellValue(column.index, rowIndex)
						if (categoryValue === undefined) continue
						if (!values.some((v) => v === categoryValue)) values.push(categoryValue)
					}
					values.sort((a, b) => a.localeCompare(b))
					return values.map((value, i) => ({ code: zeroPadding(i, 3), name: value }))
				})

				const scoreColumnList = flatten(scoreColumns.map((column) => columnList.find((col) => col.name === column)))
				if (scoreColumnList.length > 0) {
					postData.score = scoreColumnList.map(
						(column, i) => ({ sequence: i, title: column.label })
					)
				}

				for(let rowIndex = 1; rowIndex < 999999; rowIndex++) {
					const identifierValue = getCellValue(identifierIndex, rowIndex)
          if (identifierValue === undefined) continue
          
          let recordIdentifier: string | null = null
          if (typeof identifierValue === 'string') {
            recordIdentifier = identifierValue
          } else if (typeof identifierValue === 'number') {
            recordIdentifier = `${identifierValue}`
          }
          if (!recordIdentifier) continue
  
          if (recordIdentifier.endsWith(PERCENT_SUFFIX)) {
            recordIdentifier = recordIdentifier.substring(0, recordIdentifier.length - PERCENT_SUFFIX.length)
          }
          if (identifier === 'kojin_cd') {
            if (recordIdentifier.length < 6) recordIdentifier = Array((6 - recordIdentifier.length) + 1).join('0') + recordIdentifier
            if (recordIdentifier.length > 6) recordIdentifier = recordIdentifier.substring(recordIdentifier.length - 6)
          }
          let recordExtend: { rate: number } | null = null
          if (extendIndex !== null) {
						const extendValue = getCellValue(extendIndex, rowIndex)
						if (extendValue !== undefined && extendValue !== '' && extendValue !== '-' && extendValue !== '0') {
							const rate = safeParseFloat(extendValue)
							if (rate !== undefined && rate > 0) {
								recordExtend = { rate }
							}
						}
          }
          let recordTargetType: { code: number, name: string } | null = null
          if (targetTypeIndex !== null) {
						const targetTypeValue = getCellValue(targetTypeIndex, rowIndex)
						if (targetTypeValue !== undefined) {
							const targetTypeText = (typeof targetTypeValue === 'string') ? targetTypeValue
								: (typeof targetTypeValue === 'number') ? `${targetTypeValue}` : null
							if (targetTypeText !== null) {
								const code = targetTypeConditions.indexOf(targetTypeValue)
								if (code >= 0) {
									const name = targetTypeValueMap[targetTypeValue]
									recordTargetType = {
										code: code + 1,
										name: (name && name.length > 0) ? name : targetTypeValue
									}
								}
							}
						}
          }
					const recordCategory = (categoryColumnList.length > 0) ? 
						categoryColumnList.flatMap(
							(column, i) => {
								const categoryValue = getCellValue(column.index, rowIndex)
								if (categoryValue === undefined) return []
								const category = categories[i].find(({ name }) => name === categoryValue)
								if (!category) return []
								return {
									sequence: i,
									code: category.code,
									name: category.name
								}
							}
						) : undefined
					
					const recordScore = (scoreColumnList.length > 0) ? 
						scoreColumnList.flatMap(
							(column, i) => {
								const scoreValue = getCellValue(column.index, rowIndex)
								return (scoreValue === undefined) ? [] : {
									sequence: i,
									score: scoreValue
								}
							}
						) : undefined

					const at = postData.data.find((e) => e.identifier === recordIdentifier)
          if (at) {
            at.targetType = recordTargetType;
            at.extend = recordExtend ?? undefined
						at.category = recordCategory
						at.score = recordScore
            duplicate = duplicate + 1
          } else {
            postData.data.push({
              identifier: recordIdentifier,
              targetType: recordTargetType,
							category: recordCategory,
							score: recordScore,
              extend: recordExtend ?? undefined
            })
          }
        }
      } else if (loadFrom === 'input_dcf' || loadFrom === 'input_pid') {
				postData.identifier = loadFrom === 'input_dcf' ? 'kojin_cd' : 'personal_id'
        for(const row of rows) {
          const at = postData.data.find((e) => e.identifier === row.identifier)
          if (at) {
            duplicate = duplicate + 1
          } else {
            postData.data.push({ identifier: row.identifier })
          }
        }
			}
			if (postData.data.length === 0) return
			if (duplicate > 0) {
				setConfirm({
					title: `${duplicate} / ${postData.data.length} 件の重複があります。登録してもよろしいですか？`,
					onOk: () => post(postData)
				})
			} else {
				post(postData)
			}
		},
    [
			loadFrom,
			workBook, sheetName, columnList,
			identifier, identifierColumn, extendScoreColumn,
			targetTypeColumn, targetTypeConditions, targetTypeValueMap,
			categoryColumns, scoreColumns,
			rows
		]
  )

	return {
    form: {
			// formdata
			loadFrom, setLoadFrom,
			// formdata - file
			sheetName, setSheetName,
			startRow, setStartRow,
			hasHeader, setHasHeader,
			identifier, setIdentifier,
			identifierColumn, setIdentifierColumn,
			extendScoreColumn, setExtendScoreColumn,
			targetTypeColumn, setTargetTypeColumn,
			targetTypeValueMap, setTargetTypeValueMap,
			categoryColumns, addCategoryColumn, editCategoryColumn, removeCategoryColumn,
			scoreColumns, addScoreColumn, editScoreColumn, removeScoreColumn,
			// formdata - input
			rowText, setRowText,
		},
		loading, setLoading,
		file, setFile,
		workBook, setWorkBook,
		sheetNameList,
		columnList,
		targetTypeConditions,
		rows,
		isDisabled,
		submit,

		// dialog
		confirm, setConfirm
	}
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const cellValue = (cell: any): string | null => {
  if (cell.w !== undefined && cell.w !== null) {
    if (typeof cell.w === 'string') return cell.w;
    if (typeof cell.w === 'number') return `${cell.w}`;
  }
  return null;
}

type ColumnInfo = {
  index: number;
  name: string;
  label: string;
};

export const TargetListFileForm = ({
	form: {
		// formdata
		loadFrom, setLoadFrom,
		// formdata - file
		sheetName, setSheetName,
		startRow, setStartRow,
		hasHeader, setHasHeader,
		identifier, setIdentifier,
		identifierColumn, setIdentifierColumn,
		extendScoreColumn, setExtendScoreColumn,
		//targetTypeColumn, setTargetTypeColumn,
		//targetTypeValueMap, setTargetTypeValueMap,
		categoryColumns, addCategoryColumn, editCategoryColumn, removeCategoryColumn,
		scoreColumns, addScoreColumn, editScoreColumn, removeScoreColumn,
		// formdata - input_dcf/input_pid
		rowText, setRowText
	},
	setLoading,
	file, setFile,
	sheetNameList, setWorkBook,
	columnList,
	//targetTypeConditions,
	onChoiceFileTitle
}: ReturnType<typeof useTargetListFileForm> & {
	onChoiceFileTitle?: (title: string) => void
}) => {
	const { isCareNet } = useUserSession()
  const [fileError, setFileError] = useState<string | null>(null)

	const usedColumns = useMemo(
		() => [identifierColumn, extendScoreColumn, ...categoryColumns, ...scoreColumns],
		[identifierColumn, categoryColumns, scoreColumns, extendScoreColumn]
	)
	// ファイルを選択
	const onChoiceFile = useCallback(
		(file: File | null) => {
			setFileError(null)
			setWorkBook(null)
			if (file) {
				const at = file.name.lastIndexOf('.')
				const name = at < 0 ? file.name : file.name.substring(0, at)
				const ext = at < 0 ? '' : file.name.substring(at).toLowerCase()
				if (ext !== '.xlsx') {
					setFileError('ファイルの拡張子はxlsxのみ対応しています')
					return
				}
				try {
					setLoading(true)
					const reader = new FileReader()
					reader.onload = function (e) {
						if (!e?.target?.result || typeof e.target.result === 'string') return
						const unit8 = new Uint8Array(e.target.result)
						setWorkBook(XLSX.read(unit8, { type: 'array' }))
						if (onChoiceFileTitle) onChoiceFileTitle(name)
						setLoading(false)
					}
					reader.onerror = function () {
						throw new Error('ファイルの読込に失敗しました')
					}
					reader.readAsArrayBuffer(file)
				} catch (reason) {
					setFileError(`シートの読み込みに失敗しました : ${reason}`)
					setLoading(false);
					return
				}
			}
			setFile(file)
		},
		[setFileError, setFile, setWorkBook, setLoading, onChoiceFileTitle]
	)
	return (
		<FormGroup>
			<Section label='ソース' labelWidth='5em' labelProps={{ paddingY: 2 }}>
				<RadioGroup
					value={loadFrom}
					onChange={(e) => setLoadFrom(e.target.value as LoadFromType)}
				>
					<Stack direction='row' spacing={0}>
						<FormControlLabel value="file" control={<Radio />} label='EXCELファイル' />
						<FormControlLabel value="input_dcf" control={<Radio />} label='DCF個人CD'/>
						{isCareNet && <FormControlLabel value="input_pid" control={<Radio />} label='PersonalID'/>}
					</Stack>
				</RadioGroup>
			</Section>

			{loadFrom === 'file' ? (
				<Box width="100%">
					<Section label='ファイル' labelWidth='5em' labelProps={{ paddingY: 2 }}>
						<Button
							component="label" // buttonをlabelとして振る舞わせる
							variant="outlined"
							startIcon={<CloudUploadIcon />}
							sx={{
								marginBottom: 1
							}}
						>
							クリックしてファイルを選択..
							<input
								type="file"
								hidden // input要素自体は表示しない
								accept={ACCEPT_FILETYPE_EXCEL}
								onChange={(event) => {
									if (event.target.files && event.target.files[0]) {
										onChoiceFile(event.target.files[0])
									}
								}}
							/>
						</Button>
						{ fileError && <Box marginBottom={1}><Alert severity='warning'>{fileError}</Alert></Box> }
					</Section>
					
					{ file !== null && (
						<Stack direction='column'>
							<Section label='読込範囲' labelWidth='5em' labelProps={{ paddingY: 2 }}>
								<Stack direction='column' spacing={1}>
									<Stack direction='row' spacing={1} alignItems='center'>
										<Typography variant='body2' component='div'>シート</Typography>
										<FormControl size="small" sx={{ flexGrow: 1 }}>
											<Select value={sheetName} onChange={(event) => setSheetName(event.target.value)}>
												{ sheetNameList.map((sheetName, i) => <MenuItem value={sheetName} key={`sheet-${i}`}>{sheetName}</MenuItem> )}
											</Select>
										</FormControl>
									</Stack>

									<Stack direction='row' spacing={1} alignItems='center'>
										<Typography variant='body2' component='div' whiteSpace='nowrap'>先頭行</Typography>
										<NumericInput fullWidth state={startRow} setState={setStartRow}/>

										<FormControlLabel
											sx={{
												whiteSpace: 'nowrap'
											}}
											control={
												<Checkbox
													checked={hasHeader}
													size="small"
													onChange={(event) => {
														setHasHeader(event.target.checked)
													}}
												/>
											}
											label='先頭行にヘッダー'
										/>
									</Stack>
								</Stack>
							</Section>

							<Section label={isCareNet ? '個人識別' : 'DCF個人コード'} labelWidth='5em' labelProps={{ paddingY: 2 }}>
								{isCareNet && (
									<RadioGroup
										value={identifier}
										onChange={(e) => setIdentifier(e.target.value as IdentifierType)}
									>
										<Stack direction='row' spacing={0}>
											<FormControlLabel value="kojin_cd" control={<Radio />} label='DCF個人コード' />
											{isCareNet && <FormControlLabel value="personal_id" control={<Radio />} label='CareNet PersonalId'/>}
										</Stack>
									</RadioGroup>
								)}
								<FormControl size="small">
									<Select value={identifierColumn} onChange={(event) => setIdentifierColumn(event.target.value)}>
										{ columnList.map((column, i) => <MenuItem value={column.name} key={`column-${i}`}>{column.label}</MenuItem> )}
									</Select>
								</FormControl>
							</Section>

							{/*
							<Section label='分類指定' minWidth='5em' labelProps={{ paddingY: 2 }}>
								<FormControl size="small">
									<Select value={targetTypeColumn} onChange={(event) => setTargetTypeColumn(event.target.value)}>
										<MenuItem value={'none'}>なし</MenuItem>
										{ columnList.map((column, i) => <MenuItem value={column.name} key={`column-${i}`}>{column.label}</MenuItem> )}
									</Select>
								</FormControl>

								{ targetTypeConditions.map(
									(condition, i) =>
										<Stack direction='row' key={`condition-${i}`} spacing={1} alignItems='center'>
											<Typography flexGrow={1} variant='body1' align='right'>{condition}</Typography>
											<Typography variant='body1'>➡</Typography>
											<FormControl>
												<TextField
													hiddenLabel
													fullWidth
													variant='outlined'
													size='small'
													value={targetTypeValueMap[condition] ?? ''}
													onChange={
														(event) => {
															const set = { ...targetTypeValueMap };
															set[condition] = event.target.value;
															setTargetTypeValueMap(set);
														}
													}
												/>
											</FormControl>
										</Stack>
								) }
							</Section>
							*/}

							{isCareNet && (
								<Section label='拡張スコア' labelWidth='5em' labelProps={{ paddingY: 2 }}>
									<FormControl size="small">
										<Select value={extendScoreColumn} onChange={(event) => setExtendScoreColumn(event.target.value)}>
											<MenuItem value={'none'}>なし</MenuItem>
											{ columnList.map((column, i) => <MenuItem value={column.name} key={`column-${i}`}>{column.label}</MenuItem> )}
										</Select>
									</FormControl>
								</Section>
							)}

							<Section label='分類' labelWidth='5em' labelProps={{ paddingY: 2 }}>
								{categoryColumns.map((column, sequence) => (
									<FormControl size="small" key={`category-column-${sequence}`}>
										<Select
											value={column}
											onChange={(event) => {
												if (event.target.value === 'none') {
													removeCategoryColumn(sequence)
												} else {
													editCategoryColumn(sequence, event.target.value)
												}
											}}
										>
											<MenuItem value={'none'}>（取り消し）</MenuItem>
											{columnList.map(
												(column, i) => <MenuItem value={column.name} key={`column-${i}`}>{column.label}</MenuItem>
											)}
										</Select>
									</FormControl>
								))}
								<FormControl size="small">
									<Select
										value={'none'}
										onChange={(event) => {
											if (event.target.value !== 'none') addCategoryColumn(event.target.value)
										}}
										renderValue={(selected) => (selected === "none") ? <em>（選択してください）</em> : selected}
									>
										{columnList.filter(column => !usedColumns.includes(column.name)).map(
											(column, i) => <MenuItem value={column.name} key={`column-${i}`}>{column.label}</MenuItem>
										)}
									</Select>
								</FormControl>
							</Section>

							<Section label='スコア' labelWidth='5em' labelProps={{ paddingY: 2 }}>
								{scoreColumns.map((column, sequence) => (
									<FormControl size="small" key={`score-column-${sequence}`}>
										<Select
											value={column}
											onChange={(event) => {
												if (event.target.value === 'none') {
													removeScoreColumn(sequence)
												} else {
													editScoreColumn(sequence, event.target.value)
												}
											}}
										>
											<MenuItem value={'none'}>（取り消し）</MenuItem>
											{columnList.map(
												(column, i) => <MenuItem value={column.name} key={`column-${i}`}>{column.label}</MenuItem>
											)}
										</Select>
									</FormControl>
								))}
								<FormControl size="small">
									<Select
										value={'none'}
										onChange={(event) => {
											if (event.target.value !== 'none') addScoreColumn(event.target.value)
										}}
										renderValue={(selected) => (selected === "none") ? <em>（選択してください）</em> : selected}
									>
										{columnList.filter(column => !usedColumns.includes(column.name)).map(
											(column, i) => <MenuItem value={column.name} key={`column-${i}`}>{column.label}</MenuItem>
										)}
									</Select>
								</FormControl>
							</Section>
						</Stack>
					) }
				</Box>
			) : (loadFrom === "input_pid" || loadFrom === "input_dcf") ? (
				<Stack direction='column'>
					<Section label={loadFrom === "input_dcf" ? '個人CD' : "PersonalID"} labelWidth='5em' labelProps={{ paddingY: 2 }}>
						<FormControl sx={{ display: "block", marginBottom: 1 }}>
							<TextField
								hiddenLabel
								fullWidth
								multiline
								variant='outlined'
								size='small'
								rows={12}
								value={rowText}
								onChange={(event) => setRowText(event.target.value)}
							/>
						</FormControl>
					</Section>
				</Stack>
			) : <></> }
		</FormGroup>
	)
}
